import { z } from 'zod';

// ---------------------------------------------------------------------------
// 1. FIRe State (existing, refined)
// ---------------------------------------------------------------------------

export const FIReStateSchema = z.object({
  repNum: z.number().min(0),
  memory: z.number().min(0).max(1),
  lastRepDate: z.number().positive(),
  learningSpeed: z.number().min(0.5).max(2.0),
});

// ---------------------------------------------------------------------------
// 2. Session History (STORAGE_KEYS.SESSION_HISTORY)
//    Array of SessionMetrics
// ---------------------------------------------------------------------------

const WeaknessItemSchema = z.object({
  lessonId: z.string(),
  lessonTitle: z.string(),
  exerciseType: z.string(),
  errorCount: z.number(),
  totalAttempts: z.number(),
  grammarPoint: z.string().optional(),
});

const NextStepSchema = z.object({
  type: z.enum(['review-words', 'review-grammar', 'next-lesson', 'practice-type']),
  title: z.string(),
  description: z.string(),
  link: z.string(),
});

const SessionMetricsSchema = z.object({
  sessionId: z.string(),
  timestamp: z.number(),
  accuracy: z.number(),
  totalExercises: z.number(),
  correctCount: z.number(),
  bestStreak: z.number(),
  totalTimeMs: z.number(),
  avgTimePerExercise: z.number(),
  weaknesses: z.array(WeaknessItemSchema),
  nextSteps: z.array(NextStepSchema),
});

export const SessionHistorySchema: z.ZodType<z.infer<typeof SessionMetricsSchema>[]> = z.array(SessionMetricsSchema);


// ---------------------------------------------------------------------------
// 8. Daily Puzzle State (STORAGE_KEYS.DAILY_PUZZLE)
// ---------------------------------------------------------------------------

export const DailyPuzzleStateSchema = z.object({
  date: z.string(),
  puzzleType: z.enum(['word-puzzle', 'mystery-sentence', 'speed-round', 'bonus-challenge']),
  completed: z.boolean(),
  score: z.number().min(0),
  perfectBonus: z.boolean(),
});

// ---------------------------------------------------------------------------
// 9. Daily Quest State (STORAGE_KEYS.DAILY_QUESTS)
// ---------------------------------------------------------------------------

const DailyQuestSchema = z.object({
  id: z.string(),
  type: z.enum(['streak', 'learn', 'review', 'accuracy', 'duration', 'sarf', 'irab', 'reading', 'dictation', 'listening']),
  title: z.string(),
  titleAr: z.string(),
  target: z.number(),
  progress: z.number(),
  reward: z.number(),
  completed: z.boolean(),
  completedAt: z.number().optional(),
});

export const DailyQuestStateSchema = z.object({
  date: z.string(),
  candidates: z.array(DailyQuestSchema).default([]),
  selectedIds: z.array(z.string()).default([]),
  quests: z.array(DailyQuestSchema),
  rerollUsed: z.boolean().default(false),
  totalCompleted: z.number().min(0),
});

// ---------------------------------------------------------------------------
// 10. Notification Settings (STORAGE_KEYS.NOTIFICATION_SETTINGS)
// ---------------------------------------------------------------------------

export const NotificationSettingsSchema = z.object({
  enabled: z.boolean(),
  streakReminder: z.boolean(),
  streakReminderTime: z.string(),
  reviewReminder: z.boolean(),
  reviewThreshold: z.number().min(0),
});

// ---------------------------------------------------------------------------
// 7. Progress Data (STORAGE_KEYS.PROGRESS)
//     Complex nested structure — use passthrough for deep nested records
// ---------------------------------------------------------------------------

const ProgressStatsSchema = z.object({
  totalExercisesAttempted: z.number().min(0),
  totalCorrect: z.number().min(0),
  currentAnswerStreak: z.number().min(0),
  bestAnswerStreak: z.number().min(0),
  currentPracticeStreak: z.number().min(0),
  bestPracticeStreak: z.number().min(0),
  lastPracticeDate: z.string().nullable(),
});

/**
 * ProgressData schema — validates top-level shape and stats block.
 * Sub-records (exerciseResults, wordMastery, lessonProgress) use
 * z.record(z.string(), z.any()) to avoid type conflicts with the
 * deeply-nested TS interfaces that carry many optional fields.
 */
export const ProgressDataSchema = z.object({
  version: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
  exerciseResults: z.record(z.string(), z.any()),
  wordMastery: z.record(z.string(), z.any()),
  lessonProgress: z.record(z.string(), z.any()),
  stats: ProgressStatsSchema,
});

// ---------------------------------------------------------------------------
// 14. Onboarding Data (STORAGE_KEYS.ONBOARDING)
// ---------------------------------------------------------------------------

export const OnboardingDataSchema = z.object({
  experience: z.enum(['experienced', 'beginner', 'letters-only', 'expert']),
  goal: z.enum(['quran', 'classical-texts', 'studies-support', 'general']),
  timeCommitment: z.enum(['15min', '30min', '60min']),
  placementResult: z.object({
    score: z.number(),
    total: z.number(),
    recommendedBook: z.number().min(1).max(8),
    recommendedLesson: z.number(),
    strengths: z.array(z.string()),
    weaknesses: z.array(z.string()),
  }).optional(),
  microLessonScore: z.number().optional(),
  startingBook: z.number().min(1).max(8),
  startingLesson: z.number(),
  dailyLessonGoal: z.number(),
});

// ---------------------------------------------------------------------------
// 9. Boolean / number preferences
// (DARK_MODE, SOUND_ENABLED, TTS_ENABLED, TTS_AUTOPLAY are boolean;
//  TTS_SPEED is number)
// ---------------------------------------------------------------------------

export const BooleanSchema = z.boolean();
export const NumberSchema = z.number();

// ---------------------------------------------------------------------------
// Schema registry: maps storage keys to their Zod schemas.
// Used by getValidatedItem to look up the right schema automatically.
// ---------------------------------------------------------------------------

export const STORAGE_SCHEMAS: Record<string, z.ZodSchema> = {
  madina_progress: ProgressDataSchema,
  madina_session_history: SessionHistorySchema,
  madina_daily_puzzle: DailyPuzzleStateSchema,
  madina_notification_settings: NotificationSettingsSchema,
  'madina-onboarding': OnboardingDataSchema,
  madina_dark_mode: BooleanSchema,
  madina_sound_enabled: BooleanSchema,
  madina_tts_enabled: BooleanSchema,
  madina_tts_autoplay: BooleanSchema,
  madina_tts_speed: NumberSchema,
};
